import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { useCurrency } from '@/hooks/useCurrency';
import { getExcelCurrencyNumberFormat } from '@/utils/currency';
import { TablePaginationBar } from '@/components/shared/TablePaginationBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';

export type ExportCellType = 'text' | 'integer' | 'number' | 'currency' | 'date';

export interface Column {
  header: string | React.ReactNode;
  accessor: string;
  cell?: (value: any, item: any) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  sortValue?: (value: any, item: any) => any;
  /** Cabecera en Excel cuando `header` es JSX u otro nodo. */
  exportHeader?: string;
  exportValue?: (value: any, item: any) => string | number | Date | null | undefined;
  /** Tipo de celda en Excel (número, fecha, moneda, etc.). */
  exportType?: ExportCellType;
  /** Alineación de cabecera y celdas (por defecto izquierda). */
  align?: 'left' | 'right' | 'center';
  /** Números alineados para menos “salto” visual al refrescar (solo afecta al render por defecto sin `cell`). */
  numeric?: boolean;
}

interface ExternalPagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

interface ExternalSorting {
  field: string | null;
  direction: 'asc' | 'desc';
  onSort: (field: string) => void;
}

interface DataTableProps {
  columns: Column[];
  data: any[];
  pagination?: boolean;
  externalPagination?: ExternalPagination;
  externalSorting?: ExternalSorting;
  onRowClick?: (item: any) => void;
  allowExport?: boolean;
  exportFilename?: string;
  /** Nombre de la hoja en el .xlsx (por defecto i18n `common.exportSheetName`). */
  exportSheetName?: string;
  onExport?: () => Promise<any[]>; // Función personalizada para obtener todos los datos para exportar
  rowClassName?: string | ((item: any) => string);
  compact?: boolean;
  /** Permite arrastrar el borde derecho de cada cabecera para cambiar el ancho. */
  resizableColumns?: boolean;
  loading?: boolean;
  pageSize?: number;
}

function parseWidthPx(width: string | undefined): number | undefined {
  if (!width) return undefined;
  const trimmed = String(width).trim();
  // Solo px explícitos o número puro; no interpretar "7.5rem" como 7.5px.
  if (/^\d+(\.\d+)?px$/i.test(trimmed)) {
    const n = Number.parseFloat(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const n = Number.parseFloat(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function normalizeExportValue(
  exported: string | number | Date | null | undefined,
  exportType?: ExportCellType,
): string | number | Date | '' {
  if (exported === null || exported === undefined) return '';

  if (exportType === 'date') {
    if (exported instanceof Date) {
      return Number.isNaN(exported.getTime()) ? '' : exported;
    }
    if (typeof exported === 'string' && exported.trim()) {
      const normalized = exported.includes('T') ? exported : `${exported}T00:00:00`;
      const date = new Date(normalized);
      return Number.isNaN(date.getTime()) ? '' : date;
    }
    return '';
  }

  if (exportType === 'integer' || exportType === 'number' || exportType === 'currency') {
    if (typeof exported === 'number' && !Number.isNaN(exported)) return exported;
    if (typeof exported === 'string') {
      const trimmed = exported.trim();
      if (!trimmed || trimmed === '—' || trimmed === '-') return '';
      const parsed = Number(trimmed.replace(/[^\d.,-]/g, '').replace(',', '.'));
      return Number.isNaN(parsed) ? '' : parsed;
    }
    return '';
  }

  return exported;
}

function applyWorksheetExportFormats(
  ws: XLSX.WorkSheet,
  columns: Column[],
  rowCount: number,
  currencySymbol?: string,
) {
  const formatByType: Partial<Record<ExportCellType, string>> = {
    integer: '#,##0',
    number: '#,##0.##',
    currency: getExcelCurrencyNumberFormat(currencySymbol ?? '€'),
    date: 'dd/mm/yyyy',
  };

  columns.forEach((col, colIndex) => {
    const exportType = col.exportType;
    if (!exportType || exportType === 'text') return;

    const numberFormat = formatByType[exportType];
    for (let rowIndex = 1; rowIndex <= rowCount; rowIndex += 1) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
      const cell = ws[address];
      if (!cell || cell.v === '' || cell.v === undefined || cell.v === null) continue;

      if (exportType === 'date') {
        const dateValue =
          cell.v instanceof Date
            ? cell.v
            : new Date(
                typeof cell.v === 'string' && !cell.v.includes('T')
                  ? `${cell.v}T00:00:00`
                  : String(cell.v),
              );
        if (Number.isNaN(dateValue.getTime())) continue;
        cell.t = 'd';
        cell.v = dateValue;
        if (numberFormat) cell.z = numberFormat;
        continue;
      }

      const numericValue = typeof cell.v === 'number' ? cell.v : Number(cell.v);
      if (Number.isNaN(numericValue)) continue;
      cell.t = 'n';
      cell.v = numericValue;
      if (numberFormat) cell.z = numberFormat;
    }
  });
}

export function DataTable({
  columns,
  data,
  pagination = true,
  externalPagination,
  externalSorting,
  onRowClick,
  allowExport = false,
  exportFilename = 'export',
  exportSheetName,
  onExport,
  rowClassName,
  compact = false,
  resizableColumns = false,
  loading = false,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
}: DataTableProps) {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(pageSize);
  const [exporting, setExporting] = useState(false);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const { t } = useTranslation();
  const { symbol: currencySymbol } = useCurrency();
  const resizingRef = useRef<{
    accessor: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  const useFixedLayout = compact || resizableColumns;

  useEffect(() => {
    if (!resizableColumns) return;
    setColumnWidths((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const col of columns) {
        if (next[col.accessor] != null) continue;
        const fromProp = parseWidthPx(col.width);
        if (fromProp != null) {
          next[col.accessor] = fromProp;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [columns, resizableColumns]);

  const resolveColumnWidth = useCallback(
    (column: Column): number | undefined => {
      if (columnWidths[column.accessor] != null) {
        return columnWidths[column.accessor];
      }
      return parseWidthPx(column.width);
    },
    [columnWidths],
  );

  const handleColumnResizeStart = useCallback(
    (accessor: string, startX: number, startWidth: number) => {
      resizingRef.current = { accessor, startX, startWidth };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const onMove = (event: MouseEvent) => {
        const active = resizingRef.current;
        if (!active) return;
        const delta = event.clientX - active.startX;
        const nextWidth = Math.max(56, Math.round(active.startWidth + delta));
        setColumnWidths((prev) => ({
          ...prev,
          [active.accessor]: nextWidth,
        }));
      };

      const onUp = () => {
        resizingRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [],
  );

  // Determinar si usar paginación local o externa
  const useExternalPagination = externalPagination !== undefined;
  const useLocalPagination = pagination && !useExternalPagination;
  const itemsPerPage = useExternalPagination
    ? externalPagination!.pageSize
    : localPageSize;

  useEffect(() => {
    if (!useExternalPagination) {
      setLocalPageSize(pageSize);
    }
  }, [pageSize, useExternalPagination]);

  // Al reducir el dataset (p. ej. filtros), evitar página vacía si currentPage > totalPages.
  useEffect(() => {
    if (useExternalPagination) return;
    const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));
    setCurrentPage((prev) => (prev > totalPages ? totalPages : prev));
  }, [data.length, useExternalPagination, itemsPerPage]);
  
  // Determinar si usar ordenamiento local o externo
  const useExternalSorting = externalSorting !== undefined;

  const handleSort = (accessor: string) => {
    // Si hay ordenamiento externo, usar el handler externo
    if (useExternalSorting && externalSorting) {
      externalSorting.onSort(accessor);
      return;
    }
    
    // Ordenamiento local
    if (sortField === accessor) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(accessor);
      setSortDirection('asc');
    }
  };

  // Memoizar el ordenamiento para evitar recalcular en cada render
  // Si hay ordenamiento externo, NO aplicar ordenamiento local (los datos ya vienen ordenados del servidor)
  const sortedData = useMemo(() => {
    if (useExternalSorting) {
      // Con ordenamiento externo, los datos ya vienen ordenados del servidor
      return data;
    }
    
    if (!sortField) return data;
    
    // Crear una copia y ordenar
    const sorted = [...data].sort((a, b) => {
      const column = columns.find(col => col.accessor === sortField);
      const aValue = column?.sortValue ? column.sortValue(a[sortField], a) : a[sortField];
      const bValue = column?.sortValue ? column.sortValue(b[sortField], b) : b[sortField];

      if (aValue === bValue) return 0;
      
      const comparison = aValue > bValue ? 1 : -1;
      return sortDirection === 'desc' ? comparison * -1 : comparison;
    });
    
    return sorted;
  }, [data, sortField, sortDirection, columns, useExternalSorting]);

  // Memoizar la paginación
  // Si hay paginación externa, NO aplicar paginación local (los datos ya vienen paginados del servidor)
  const paginatedData = useMemo(() => {
    if (useExternalPagination) {
      // Con paginación externa, los datos ya vienen paginados del servidor
      // Solo aplicamos ordenamiento local si es necesario (hasta implementar ordenamiento en servidor)
      return sortedData;
    }
    if (!useLocalPagination) return sortedData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, useExternalPagination, useLocalPagination, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    if (useExternalPagination && externalPagination) {
      return externalPagination.totalPages;
    }
    return Math.ceil(sortedData.length / itemsPerPage);
  }, [sortedData.length, itemsPerPage, useExternalPagination, externalPagination]);

  const paginationState = useMemo(() => {
    if (useExternalPagination && externalPagination) {
      const page = externalPagination.currentPage;
      const total = externalPagination.totalPages;
      const totalItems = externalPagination.totalCount;
      const size = externalPagination.pageSize;
      const from = totalItems === 0 ? 0 : (page - 1) * size + 1;
      const to = Math.min(page * size, totalItems);
      return { page, total, totalItems, from, to, external: true };
    }
    const page = currentPage;
    const total = totalPages;
    const totalItems = data.length;
    const from = totalItems === 0 ? 0 : (page - 1) * itemsPerPage + 1;
    const to = Math.min(page * itemsPerPage, totalItems);
    return { page, total, totalItems, from, to, external: false };
  }, [
    useExternalPagination,
    externalPagination,
    currentPage,
    totalPages,
    data.length,
    itemsPerPage,
  ]);

  const handleExport = async () => {
    try {
      setExporting(true);
      
      // Si hay función personalizada de exportación, usarla para obtener todos los datos
      let exportData = data;
      if (onExport) {
        exportData = await onExport();
      }
      
      const resolveExportHeader = (col: Column): string =>
        col.exportHeader ??
        (typeof col.header === 'string' ? col.header : String(col.accessor));

      const headers = columns.map(resolveExportHeader);

      // Convertir datos a formato para Excel
      const excelData = exportData.map(item => {
        const row: Record<string, string | number | Date> = {};
        columns.forEach(col => {
          const rawValue = item[col.accessor];
          const headerName = resolveExportHeader(col);

          let value: string | number | Date | '';
          if (col.exportValue) {
            const exported = col.exportValue(rawValue, item);
            value = normalizeExportValue(exported, col.exportType);
          } else if (rawValue === null || rawValue === undefined) {
            value = '';
          } else if (typeof rawValue === 'object') {
            if (rawValue.name !== undefined) {
              value = rawValue.name;
            } else if (rawValue.value !== undefined) {
              value = normalizeExportValue(rawValue.value, col.exportType);
            } else if (rawValue.units !== undefined) {
              value = normalizeExportValue(rawValue.units, col.exportType);
            } else {
              value = JSON.stringify(rawValue);
            }
          } else if (col.exportType) {
            value = normalizeExportValue(rawValue, col.exportType);
          } else {
            value = rawValue;
          }

          row[headerName] = value;
        });
        return row;
      });
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData, { cellDates: true });
      applyWorksheetExportFormats(ws, columns, excelData.length, currencySymbol);
      
      // Ajustar el ancho de las columnas automáticamente
      const columnWidths = headers.map(() => ({ wch: 15 }));
      ws['!cols'] = columnWidths;
      
      // Agregar la hoja al libro
      const sheetName = exportSheetName ?? t('common.exportSheetName');
      XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
      
      // Generar el nombre del archivo
      const fileName = `${exportFilename}.xlsx`;
      
      // Descargar el archivo
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setExporting(false);
    }
  };

  const getRowClassName = (item: any) => {
    if (typeof rowClassName === 'string') {
      return rowClassName;
    } else if (typeof rowClassName === 'function') {
      return rowClassName(item);
    }
    return '';
  };

  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-lg bg-white shadow-sm">
      <div className="min-w-0 max-w-full overflow-x-auto">
        <table
          className={`min-w-full divide-y divide-gray-200 font-variant-numeric tabular-nums ${useFixedLayout ? 'text-xs table-fixed' : ''}`}
          style={useFixedLayout ? { tableLayout: 'fixed' } : undefined}
        >
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column, index) => {
                const align = column.align ?? 'left';
                const thAlign =
                  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
                const widthPx = resolveColumnWidth(column);
                const widthStyle = widthPx
                  ? { width: `${widthPx}px`, minWidth: `${widthPx}px`, maxWidth: `${widthPx}px` }
                  : column.width
                    ? { width: column.width, minWidth: column.width, maxWidth: column.width }
                    : undefined;
                return (
                <th 
                  key={index}
                  scope="col"
                  className={`relative ${thAlign} font-medium text-gray-500 uppercase ${
                    compact || resizableColumns ? 'tracking-normal' : 'tracking-wider'
                  } overflow-hidden ${
                    compact || resizableColumns ? 'px-1.5 py-1.5 text-[10px]' : 'px-6 py-3 text-xs'
                  } ${column.sortable ? 'cursor-pointer' : ''}`}
                  style={widthStyle}
                  onClick={(e) => {
                    // No ordenar si el click fue en un elemento interactivo (input, button, etc.)
                    const target = e.target as HTMLElement;
                    const isInteractiveElement = target.tagName === 'INPUT' || 
                                                 target.tagName === 'BUTTON' || 
                                                 target.tagName === 'SELECT' ||
                                                 target.closest('input, button, select') ||
                                                 target.closest('[data-column-resize-handle]');
                    
                    if (column.sortable && !isInteractiveElement) {
                      handleSort(column.accessor);
                    }
                  }}
                >
                  <div
                    className={`flex items-center min-w-0 ${
                      align === 'right'
                        ? 'justify-end'
                        : align === 'center'
                          ? 'justify-center'
                          : 'justify-start'
                    }`}
                  >
                    {typeof column.header === 'string' ? (
                      <span className="truncate whitespace-nowrap">{column.header}</span>
                    ) : (
                      column.header
                    )}
                    {column.sortable && (() => {
                      // Determinar qué campo y dirección mostrar
                      const currentSortField = useExternalSorting && externalSorting 
                        ? externalSorting.field 
                        : sortField;
                      const currentSortDirection = useExternalSorting && externalSorting 
                        ? externalSorting.direction 
                        : sortDirection;
                      
                      if (currentSortField === column.accessor) {
                        return (
                          <span className={compact || resizableColumns ? 'ml-0.5' : 'ml-1'}>
                            {currentSortDirection === 'asc' ? 
                              <ChevronUp className={compact || resizableColumns ? 'h-3 w-3' : 'h-4 w-4'} /> : 
                              <ChevronDown className={compact || resizableColumns ? 'h-3 w-3' : 'h-4 w-4'} />
                            }
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  {resizableColumns ? (
                    <span
                      data-column-resize-handle
                      role="separator"
                      aria-orientation="vertical"
                      title={t('common.resizeColumn', { defaultValue: 'Resize column' })}
                      className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize touch-none select-none hover:bg-sky-300/70"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const th = e.currentTarget.parentElement;
                        const current =
                          widthPx ??
                          (th ? Math.round(th.getBoundingClientRect().width) : 100);
                        handleColumnResizeStart(column.accessor, e.clientX, current);
                      }}
                    />
                  ) : null}
                </th>
              );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 relative">
            {loading && (
              <tr className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-10">
                <td colSpan={columns.length} className="w-full h-32 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]"></div>
                    <span className="text-xs text-gray-500 font-medium">Cargando datos...</span>
                  </div>
                </td>
              </tr>
            )}
            {paginatedData.map((item, rowIndex) => {
              const rowBg = getRowClassName(item);
              const inheritTd = rowBg ? '[&>td]:bg-inherit' : '';
              return (
              <tr 
                key={rowIndex}
                className={`${onRowClick ? 'cursor-pointer hover:bg-blue-50' : ''} ${rowBg} ${inheritTd}`.trim()}
                onClick={() => onRowClick && onRowClick(item)}
              >
                {columns.map((column, colIndex) => {
                  const align = column.align ?? 'left';
                  const tdAlign =
                    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
                  const widthPx = resolveColumnWidth(column);
                  const widthStyle = widthPx
                    ? { width: `${widthPx}px`, minWidth: `${widthPx}px`, maxWidth: `${widthPx}px` }
                    : column.width
                      ? { width: column.width, minWidth: column.width, maxWidth: column.width }
                      : undefined;
                  return (
                  <td
                    key={colIndex}
                    className={`text-gray-500 overflow-hidden ${tdAlign} ${compact || resizableColumns ? 'px-1.5 py-1 text-xs' : 'px-6 py-4 text-sm'} ${compact || resizableColumns ? '' : 'whitespace-nowrap'}`}
                    style={widthStyle}
                  >
                    {column.cell ? (
                      <div className={`min-w-0 overflow-hidden ${align === 'right' ? 'flex justify-end' : align === 'center' ? 'flex justify-center' : ''}`}>
                        {column.cell(item[column.accessor], item)}
                      </div>
                    ) : (
                      <span
                        className={`block truncate min-w-0 ${column.numeric ? 'font-variant-numeric tabular-nums' : ''}`}
                        title={String(item[column.accessor] ?? '')}
                      >
                        {item[column.accessor]}
                      </span>
                    )}
                  </td>
                );
                })}
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
      
      {(useLocalPagination || useExternalPagination) ? (
        <TablePaginationBar
          currentPage={paginationState.page}
          totalPages={paginationState.total}
          totalItems={paginationState.totalItems}
          pageSize={itemsPerPage}
          disabled={loading}
          onPageChange={(nextPage) => {
            if (paginationState.external && externalPagination) {
              externalPagination.onPageChange(nextPage);
            } else {
              setCurrentPage(nextPage);
            }
          }}
          onPageSizeChange={
            paginationState.external && externalPagination?.onPageSizeChange
              ? externalPagination.onPageSizeChange
              : useLocalPagination
                ? (size) => {
                    setLocalPageSize(size);
                    setCurrentPage(1);
                  }
                : undefined
          }
        />
      ) : null}
      
      {allowExport && (
        <div className="flex justify-end mt-4">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center px-3 py-1 bg-[#1681ba] text-white rounded text-sm hover:bg-[#1370a3] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                {t('common.exporting')}...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-1" />
                {t('common.export')}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
